import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { message } from 'antd';
import { CheckOutlined, CopyOutlined } from '@ant-design/icons';
import {
  DOG_EXPRESSIONS,
  EXPRESSION_CATALOG_VERSION,
  HUMAN_EXPRESSIONS,
  INTENSITY_ORDER,
} from '../illustration/expressionCatalog';
import { EXPRESSION_SOURCES } from '../illustration/expressionSources';
import EngineSources from './EngineSources';
import './ExpressionEnginePage.css';

const COLUMNS = [
  { intensity: 'small', scale: 'Small', band: 'Mild' },
  { intensity: 'core', scale: 'Core', band: 'The emotion' },
  { intensity: 'big', scale: 'Big', band: 'Intense' },
  { intensity: 'out-of-control', scale: 'Out of control', band: 'Extreme' },
  { intensity: 'de-escalating', scale: 'De-escalating', band: 'Resolve' },
];

const TINT = {
  small: 6,
  core: 11,
  big: 17,
  'out-of-control': 25,
};

const FAMILY = {
  happiness: 'happiness',
  sadness: 'sadness',
  anger: 'anger',
  fear: 'fear',
  surprise: 'surprise',
  disgust: 'disgust',
  interest: 'interest',
  care: 'care',
  rest: 'rest',
  anticipation: 'anticipation',
  longing: 'longing',
  joy: 'happiness',
  frustration: 'anger',
  alert: 'interest',
  hopeful: 'anticipation',
  subdued: 'longing',
};

const FACE = {
  AU1: 'Inner brow raiser',
  AU2: 'Outer brow raiser',
  AU4: 'Brow lowerer',
  AU5: 'Upper lid raiser',
  AU6: 'Cheek raiser',
  AU7: 'Lid tightener',
  AU9: 'Nose wrinkler',
  AU10: 'Upper lip raiser',
  AU12: 'Lip corner puller',
  AU14: 'Dimpler',
  AU15: 'Lip corner depressor',
  AU16: 'Lower lip depressor',
  AU17: 'Chin raiser',
  AU19: 'Tongue show',
  AU20: 'Lip stretcher',
  AU23: 'Lip tightener',
  AU24: 'Lip pressor',
  AU25: 'Lips part',
  AU26: 'Jaw drop',
  AU27: 'Mouth stretch',
  AU38: 'Nostril dilator',
  AU41: 'Lid droop',
  AU43: 'Eyes closed',
  AU45: 'Blink',
  AU53: 'Head up',
  AU54: 'Head down',
  AU57: 'Head forward',
  AU64: 'Eyes down',
  AU101: 'Inner brow raiser',
  AU109: 'Muzzle wrinkle',
  AU116: 'Lower lip depressor',
  AU143: 'Eyes closed',
  AU145: 'Blink',
  EAD101: 'Ears forward',
  EAD102: 'Ears drawn together',
  EAD103: 'Ears back',
  EAD104: 'Ear flattener',
  AD19: 'Tongue show',
  AD37: 'Nose lick',
  AD126: 'Panting',
};

function titleCase(value) {
  const text = String(value || '');
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : '';
}

function sentence(value) {
  const text = String(value || '').trim();
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : '';
}

function rowsOf(entries) {
  const order = [];
  const grouped = new Map();
  entries.forEach((entry) => {
    if (!grouped.has(entry.emotion)) {
      grouped.set(entry.emotion, []);
      order.push(entry.emotion);
    }
    grouped.get(entry.emotion).push(entry);
  });
  return order.map((emotion) => {
    const cells = grouped.get(emotion);
    return {
      emotion,
      family: FAMILY[emotion] || 'rest',
      system: cells[0]?.kind === 'dog' ? 'DogFACS' : 'FACS',
      cells: INTENSITY_ORDER.map(
        (intensity) => cells.find((entry) => entry.intensity === intensity) || null
      ),
    };
  });
}

function compactCodes(aus = []) {
  const parts = [];
  let current = null;
  aus.forEach((code) => {
    const match = String(code).match(/^(AU|EAD|AD)(\d+)$/);
    if (!match) {
      current = null;
      parts.push(String(code));
      return;
    }
    const [, prefix, num] = match;
    if (current && current.prefix === prefix) {
      current.nums.push(num);
      return;
    }
    current = { prefix, nums: [num] };
    parts.push(current);
  });
  return parts
    .map((part) => (typeof part === 'string' ? part : `${part.prefix}${part.nums.join('+')}`))
    .join('+');
}

function emptyLabel(emotion, intensity) {
  if (emotion === 'frustration' && intensity === 'out-of-control') {
    return 'N/A — kept off-model for a children’s book';
  }
  return 'N/A';
}

export function expressionCopyLine(emotion, cell, system) {
  const codes = (cell?.facs?.aus || []).join(' ');
  const face = sentence(cell?.fragment);
  const closed = /[.!?]$/.test(face) ? face : `${face}.`;
  return `${titleCase(emotion)}: ${cell?.label || ''} (${system} ${codes}); ${closed}`;
}

function CopyFaceButton({ text, name }) {
  const [copied, setCopied] = useState(false);
  const timer = useRef(null);

  useEffect(() => () => clearTimeout(timer.current), []);

  return (
    <button
      type="button"
      className="expression-engine__copy"
      aria-label={copied ? `Copied ${name}` : `Copy ${name}`}
      onClick={async (event) => {
        event.stopPropagation();
        try {
          await navigator.clipboard.writeText(text);
          setCopied(true);
          message.success('Copied');
          clearTimeout(timer.current);
          timer.current = setTimeout(() => setCopied(false), 1600);
        } catch {
          message.error('Could not copy');
        }
      }}
    >
      {copied ? <CheckOutlined /> : <CopyOutlined />}
    </button>
  );
}

function ExpressionMatrix({ title, intro, rows, onShow, onHide }) {
  return (
    <section className="expression-engine__section">
      <h2>{title}</h2>
      <p className="expression-engine__sub">{intro}</p>
      <div className="expression-engine__scroll" onScroll={onHide}>
        <div className="expression-engine__matrix" role="grid" aria-label={title}>
          <div className="expression-engine__corner">Emotion / Intensity</div>
          {COLUMNS.map((column) => (
            <div
              key={column.intensity}
              className={
                column.intensity === 'de-escalating'
                  ? 'expression-engine__col expression-engine__col--resolve'
                  : 'expression-engine__col'
              }
              role="columnheader"
            >
              <span className="expression-engine__scale">{column.scale}</span>
              <span className="expression-engine__band">{column.band}</span>
            </div>
          ))}
          {rows.map((row) => (
            <React.Fragment key={row.emotion}>
              <div
                className="expression-engine__row-head"
                style={{ '--fam': `var(--color-expression-${row.family})`, '--fam-wash': `var(--color-expression-${row.family}-wash)` }}
                role="rowheader"
              >
                <span className="expression-engine__name">
                  <span className="expression-engine__dot" aria-hidden="true" />
                  {titleCase(row.emotion)}
                </span>
                <span className="expression-engine__aus">{row.system}</span>
              </div>
              {row.cells.map((cell, index) => {
                const intensity = INTENSITY_ORDER[index];
                if (!cell) {
                  return (
                    <div key={intensity} className="expression-engine__cell expression-engine__cell--empty" role="gridcell">
                      <span>{emptyLabel(row.emotion, intensity)}</span>
                    </div>
                  );
                }
                const codes = compactCodes(cell.facs?.aus);
                const face = sentence(cell.fragment);
                const resolve = intensity === 'de-escalating';
                const copyText = expressionCopyLine(row.emotion, cell, row.system);
                return (
                  <div
                    key={cell.id}
                    className={
                      resolve
                        ? 'expression-engine__cell expression-engine__cell--resolve'
                        : 'expression-engine__cell'
                    }
                    style={{
                      '--fam': `var(--color-expression-${row.family})`,
                      '--tint': TINT[intensity] || 6,
                    }}
                    role="gridcell"
                    onMouseEnter={(event) => onShow(event.currentTarget, cell, face)}
                    onMouseLeave={onHide}
                  >
                    <span className="expression-engine__label">
                      <span className="expression-engine__nm">{cell.label}</span>
                      <span className="expression-engine__lvl">{codes}</span>
                    </span>
                    <span className="expression-engine__frag">{face}</span>
                    <CopyFaceButton text={copyText} name={`${titleCase(row.emotion)} ${cell.label}`} />
                  </div>
                );
              })}
            </React.Fragment>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function ExpressionEnginePage() {
  const humanRows = useMemo(() => rowsOf(HUMAN_EXPRESSIONS), []);
  const dogRows = useMemo(() => rowsOf(DOG_EXPRESSIONS), []);
  const [tip, setTip] = useState(null);
  const tipRef = useRef(null);

  useEffect(() => {
    document.title = 'Expression engine · Illustration Studio';
  }, []);

  useEffect(() => {
    if (!tip) return undefined;
    const hide = () => setTip(null);
    window.addEventListener('scroll', hide, true);
    return () => window.removeEventListener('scroll', hide, true);
  }, [tip]);

  useLayoutEffect(() => {
    if (!tip || !tipRef.current) return;
    const node = tipRef.current;
    const rect = tip.rect;
    const width = node.offsetWidth;
    const height = node.offsetHeight;
    const left = Math.max(8, Math.min(rect.left + rect.width / 2 - width / 2, window.innerWidth - width - 8));
    let top = rect.top - height - 10;
    if (top < 8) top = Math.min(rect.bottom + 10, window.innerHeight - height - 8);
    node.style.left = `${left}px`;
    node.style.top = `${top}px`;
  }, [tip]);

  const showTip = (node, cell, face) => {
    const codes = (cell.facs?.aus || []).map((code) => ({
      code,
      label: FACE[code] || 'Action unit',
    }));
    setTip({
      name: cell.label,
      codes,
      face,
      rect: node.getBoundingClientRect(),
    });
  };

  return (
    <div className="workflow-shell__content expression-engine-page">
      <p className="expression-engine__eyebrow">Phase 1 · faces · Updated 2026-10-21 · Catalog {EXPRESSION_CATALOG_VERSION}</p>
      <h1 className="expression-engine__title">The Expression Engine</h1>
      <p className="expression-engine__lede">
        Face catalog for people and dogs. Each cell is one entry: emotion, intensity, label, action units, and the prompt fragment. Hover or focus a face for the codes.
      </p>

      <ExpressionMatrix
        title="Human matrix"
        intro="Eleven rows, five bands each. The dashed column is the de-escalating exit. Hover a face for the action units."
        rows={humanRows}
        onShow={showTip}
        onHide={() => setTip(null)}
      />
      <ExpressionMatrix
        title="Dog matrix"
        intro="A separate DogFACS list. Disgust is omitted. An empty band has no documented canine face."
        rows={dogRows}
        onShow={showTip}
        onHide={() => setTip(null)}
      />

      <p className="expression-engine__note">
        <strong>No disgust row for dogs.</strong> DogFACS does not define a disgust face. Aversion is a behavior, not a cell.
      </p>
      <EngineSources
        id="expression-sources-title"
        intro="The grid is borrowed from four lines of work. The face sentence in each cell is written here."
        sources={EXPRESSION_SOURCES}
      />
      <p className="expression-engine__footer">
        Expression engine · catalog {EXPRESSION_CATALOG_VERSION} · 2026-10-21
      </p>

      {tip ? (
        <div className="expression-engine__tip" ref={tipRef} role="tooltip">
          <h3>{tip.name}</h3>
          {tip.codes.length ? (
            <ul>
              {tip.codes.map((item) => (
                <li key={item.code}>
                  <code>{item.code}</code>
                  <span>{item.label}</span>
                </li>
              ))}
            </ul>
          ) : null}
          <p>{tip.face}</p>
        </div>
      ) : null}
    </div>
  );
}
